import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import LinkedInAppInstruction from "./LinkedInAppInstruction";
import LinkedInAppPreview from "./LinkedInAppPreview";
import LinkedInCredentialsForm from "./LinkedInCredentialsForm";
import LinkedInAuthorization from "./LinkedInAuthorization";
import LinkedInConsent from "./LinkedInConsent";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getLinkedInClientData, updateLinkedInClientData } from "./../../../../../revampCRUD/linkedInShare.crud";
import customToast from "../../../../../components/common/basic/Toast";
import LoadingSpinner from '../../../../../revampComponents/Atom/Loading';
import FormButtons from '../../../../../revampComponents/Molecule/Buttons/FormButtons';
import "./LinkedInShare.css";

const LinkedInShareSetup = ({ eventDetails, isRestricted }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [initialValues, setInitialValues] = useState({
        linkedin_client_id: null,
        linkedin_client_secret: null,
        consent: false,
        appPermissionConsent: false
    });

    const resetData = () => {
        formik.setValues(initialValues);
    };

    const saveData = async (values) => {
        if (isRestricted) return;
        try {
            setSaving(true);
            await updateLinkedInClientData(eventDetails.event_id, {
                linkedin_client_id: values.linkedin_client_id,
                linkedin_client_secret: values.linkedin_client_secret
            });
            customToast.success("LinkedIn client details saved successfully!");
        } catch (err) {
            console.error(err);
            customToast.error("Could not save LinkedIn client details!");
        } finally {
            setSaving(false);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object({
            linkedin_client_id: Yup.string().nullable().required("Client ID is required"),
            linkedin_client_secret: Yup.string().nullable().required("Client Secret is required"),
            consent: Yup.boolean().required("Please check the box to proceed"),
            appPermissionConsent: Yup.boolean().required("Please check the box to proceed")
        }),
        onSubmit: saveData
    });

    useEffect(() => {
        if (isRestricted) return;
        const fetchAppDetails = async () => {
            try {
                const res = await getLinkedInClientData(eventDetails.event_id);
                formik.setValues({
                    linkedin_client_id: res.data.linkedin_client_id,
                    linkedin_client_secret: res.data.linkedin_client_secret,
                    consent: false,
                    appPermissionConsent: false
                });
                setInitialValues({
                    linkedin_client_id: res.data.linkedin_client_id,
                    linkedin_client_secret: res.data.linkedin_client_secret,
                    consent: false,
                    appPermissionConsent: false
                });
            } catch (error) {
                customToast.error("Could not get LinkedIn client details!");
            } finally {
                setLoading(false);
            }
        };
        fetchAppDetails();
    }, [eventDetails.event_id]);

    if (loading) return <div className='row w-100 position-relative'><LoadingSpinner /></div>

    return (
        <div className="linkedin-share-setup">
            <div className="linkedin-share-content">
                <LinkedInAppInstruction />
                <LinkedInAppPreview />
            </div>
            <LinkedInCredentialsForm formik={formik} isRestricted={isRestricted} eventId={eventDetails.event_id} />
            <LinkedInAuthorization />
            <LinkedInConsent formik={formik} isRestricted={isRestricted} />
            <div className="btn-wrap-ticket-settings">
                <FormButtons
                    onCancel={resetData}
                    onSave={formik.handleSubmit}
                    cancelBtnContent={`Cancel`}
                    isLoading={saving}
                    saveBtnDisabled={isRestricted || saving || !formik.dirty || !formik.isValid || !formik.values.consent || !formik.values.appPermissionConsent}
                />
            </div>
        </div>
    );
};

LinkedInShareSetup.propTypes = {
    eventDetails: PropTypes.shape({
        event_id: PropTypes.string
    }),
    isRestricted: PropTypes.bool
};

export default LinkedInShareSetup;
